import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { PAYMENT_HISTORY_LINE_RULES } from '../IMessageBubbleInterface';
import { capitalizeWithReplacements, formatCurrency, parsePaymentHistory } from '../MessageBubbleHelpers';

export const PaymentHistoryCard: React.FC<{
    paymentHistory: ReturnType<typeof parsePaymentHistory>;
}> = ({ paymentHistory }) => {
    const [expandedPaymentHistoryKey, setExpandedPaymentHistoryKey] = React.useState<string | null>(null);
    if (!paymentHistory) return null;
    const hasMeaningfulValue = (value: string) => {
        const normalized = (value || '').trim().toLowerCase();
        return Boolean(
            normalized && normalized !== '-' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'none',
        );
    };
    const shouldFormatCurrency = (value: string) => /^\$?\d[\d,]*(\.\d+)?$/.test((value || '').trim());
    const formatPaymentHistoryLine = (value: string) => capitalizeWithReplacements(value, PAYMENT_HISTORY_LINE_RULES);

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{ mb: 1, p: 0.9, borderRadius: '10px', backgroundColor: colors.greyPreset[90] }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 800, fontSize: '1rem' }}>
                        {paymentHistory.intro}
                    </Typography>
                    {paymentHistory.payments.some(payment => (payment.breakdowns || []).length > 0) && (
                        <Typography variant="caption" sx={{ color: colors.greyPreset[60], fontWeight: 600 }}>
                            Click a card to view the grey breakdown details.
                        </Typography>
                    )}
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: '999px',
                        backgroundColor: colors.fullWhite,
                        color: colors.greyPreset[60],
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {paymentHistory.payments.length} records
                </Typography>
            </Stack>

            <Stack gap={1}>
                {paymentHistory.payments.map((payment, idx) => {
                    const rowKey = `${payment.contractId}-${payment.paymentDate}-${payment.referenceNumber}-${idx}`;
                    const isExpanded = expandedPaymentHistoryKey === rowKey;
                    const hasContractId = hasMeaningfulValue(payment.contractId);
                    const primaryLabel = hasContractId ? 'Contract ID' : 'Invoice Number';
                    const primaryValue = hasContractId ? payment.contractId : payment.invoiceNumber;
                    const breakdownRows =
                        payment.breakdowns && payment.breakdowns.length > 0
                            ? payment.breakdowns
                            : [
                                  {
                                      dueDate: payment.dueDate,
                                      invoiceNumber: payment.invoiceNumber,
                                      transactionType: payment.transactionType,
                                      invoiceMethod: payment.invoiceMethod,
                                      status: payment.status,
                                      appliedAmount: payment.appliedAmount,
                                  },
                              ];
                    const hasBreakdownRows = breakdownRows.some(
                        breakdown =>
                            hasMeaningfulValue(breakdown.dueDate) ||
                            hasMeaningfulValue(breakdown.transactionType) ||
                            hasMeaningfulValue(breakdown.invoiceMethod) ||
                            hasMeaningfulValue(breakdown.appliedAmount),
                    );
                    const normalizedStatus = (payment.status || '').toLowerCase();
                    const isCompleted = normalizedStatus.includes('completed');

                    return (
                        <Box
                            key={rowKey}
                            sx={{
                                border: `1px solid ${isExpanded ? colors.bluePreset[10] : colors.greyPreset[20]}`,
                                borderRadius: '16px',
                                overflow: 'hidden',
                                backgroundColor: colors.fullWhite,
                                boxShadow: isExpanded
                                    ? '0 10px 24px rgba(16, 24, 40, 0.10)'
                                    : '0 2px 8px rgba(16, 24, 40, 0.06)',
                            }}
                        >
                            <Box
                                onClick={() => {
                                    setExpandedPaymentHistoryKey(prev => (prev === rowKey ? null : rowKey));
                                }}
                                sx={{
                                    p: 1.25,
                                    cursor: 'pointer',
                                    borderLeft: `4px solid ${isExpanded ? colors.bluePreset[10] : colors.greyPreset[30]}`,
                                    '&:hover': {
                                        backgroundColor: colors.greyPreset[90],
                                    },
                                }}
                            >
                                <Stack gap={1.1}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1.35fr 0.75fr' },
                                            gap: 1,
                                            alignItems: 'start',
                                        }}
                                    >
                                        <Stack gap={0.85} sx={{ minWidth: 0 }}>
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[50],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                    }}
                                                >
                                                    {primaryLabel}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {primaryValue || '-'}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[50],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                    }}
                                                >
                                                    Reference Number
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 700,
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {payment.referenceNumber || '-'}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack
                                            alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                                            gap={0.8}
                                            sx={{ minWidth: 0 }}
                                        >
                                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[50],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                    }}
                                                >
                                                    Amount
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    component={'h1'}
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {shouldFormatCurrency(payment.amount)
                                                        ? formatCurrency(payment.amount)
                                                        : hasMeaningfulValue(payment.amount)
                                                          ? payment.amount
                                                          : '-'}
                                                </Typography>
                                            </Box>

                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '999px',
                                                    backgroundColor: isCompleted ? '#DDF3E6' : colors.greyPreset[90],
                                                    color: isCompleted ? '#1F7A4D' : colors.greyPreset[60],
                                                    fontWeight: 700,
                                                    textTransform: 'capitalize',
                                                }}
                                            >
                                                {payment.status || 'Unknown'}
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    <Box
                                        sx={{
                                            borderRadius: '12px',
                                            border: `1px solid ${colors.greyPreset[30]}`,
                                            backgroundColor: colors.greyPreset[90],
                                            px: 1,
                                            py: 0.85,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 0.8,
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}
                                    >
                                        {[
                                            ['Payment Effective Date', payment.paymentDate],
                                            ['Method', payment.method],
                                        ].map(([label, value]) => (
                                            <Box key={`${rowKey}-${label}`} sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: colors.greyPreset[60],
                                                        fontWeight: 700,
                                                        display: 'block',
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: colors.fullBlack,
                                                        fontWeight: 700,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {value || '-'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {hasBreakdownRows && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                            sx={{
                                                pt: 0.85,
                                                borderTop: `1px dashed ${colors.greyPreset[30]}`,
                                                mt: 0.25,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ color: colors.greyPreset[60], fontWeight: 700 }}
                                            >
                                                {isExpanded ? 'Hide payment breakdown' : 'View payment breakdown'}
                                            </Typography>
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRight: `2px solid ${colors.greyPreset[60]}`,
                                                    borderBottom: `2px solid ${colors.greyPreset[60]}`,
                                                    transform: isExpanded ? 'rotate(-135deg)' : 'rotate(45deg)',
                                                    transition: 'transform 0.2s ease',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>

                            {isExpanded && hasBreakdownRows && (
                                <Box
                                    sx={{
                                        backgroundColor: colors.greyPreset[90],
                                        borderTop: `1px solid ${colors.greyPreset[30]}`,
                                        p: 1.2,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: colors.greyPreset[50],
                                            fontWeight: 800,
                                            display: 'block',
                                            mb: 0.9,
                                        }}
                                    >
                                        Payment Breakdown
                                    </Typography>

                                    <Stack gap={0.9}>
                                        {breakdownRows.map((breakdown, breakdownIndex) => {
                                            const breakdownStatus = (breakdown.status || '').toLowerCase();
                                            const isBreakdownCompleted = breakdownStatus.includes('completed');

                                            return (
                                                <Box
                                                    key={`${rowKey}-breakdown-${breakdownIndex}`}
                                                    sx={{
                                                        backgroundColor: colors.fullWhite,
                                                        border: `1px solid ${colors.greyPreset[30]}`,
                                                        borderRadius: '12px',
                                                        p: 1,
                                                    }}
                                                >
                                                    <Stack gap={0.8}>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="flex-end"
                                                            gap={1}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    px: 0.9,
                                                                    py: 0.2,
                                                                    borderRadius: '999px',
                                                                    backgroundColor: isBreakdownCompleted
                                                                        ? '#DDF3E6'
                                                                        : colors.greyPreset[90],
                                                                    color: isBreakdownCompleted
                                                                        ? '#1F7A4D'
                                                                        : colors.greyPreset[60],
                                                                    fontWeight: 700,
                                                                    textTransform: 'capitalize',
                                                                }}
                                                            >
                                                                {breakdown.status || '-'}
                                                            </Typography>
                                                        </Stack>

                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: {
                                                                    xs: '1fr',
                                                                    sm: 'repeat(3, minmax(0, 1fr))',
                                                                },
                                                                gap: 0.8,
                                                            }}
                                                        >
                                                            {[
                                                                ['Due Date', breakdown.dueDate],
                                                                ['Invoice Number', breakdown.invoiceNumber],
                                                                ['Applied Amount', breakdown.appliedAmount],
                                                                ['Transaction Type', breakdown.transactionType],
                                                                ['Invoice Method', breakdown.invoiceMethod],
                                                            ].map(([label, value]) => (
                                                                <Box
                                                                    key={`${rowKey}-${breakdownIndex}-${label}`}
                                                                    sx={{ minWidth: 0 }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: colors.greyPreset[60],
                                                                            fontWeight: 700,
                                                                            display: 'block',
                                                                            mb: 0.2,
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: colors.fullBlack,
                                                                            fontWeight:
                                                                                label === 'Applied Amount' ? 800 : 600,
                                                                            lineHeight: 1.4,
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        {label === 'Applied Amount'
                                                                            ? shouldFormatCurrency(value)
                                                                                ? formatCurrency(value)
                                                                                : hasMeaningfulValue(value)
                                                                                  ? value
                                                                                  : '-'
                                                                            : hasMeaningfulValue(value)
                                                                              ? value
                                                                              : '-'}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {paymentHistory.followUpLines?.length > 0 && (
                <Stack component="ul" sx={{ m: 0, mt: 0.8, p: 0, gap: 0.2, listStyle: 'none' }}>
                    {paymentHistory.followUpLines.map((line: string, index: number) => (
                        <Typography
                            key={`payment-follow-up-${index}`}
                            variant="body2"
                            component="li"
                            sx={{ color: colors.fullBlack }}
                        >
                            {formatPaymentHistoryLine(line)}
                        </Typography>
                    ))}
                </Stack>
            )}
            {(!paymentHistory.followUpLines || paymentHistory.followUpLines.length === 0) && (
                <Typography variant="body2" sx={{ mt: 0.8, color: colors.fullBlack }}>
                    {formatPaymentHistoryLine('Would you like to know about a specific payment?')}
                </Typography>
            )}
        </Box>
    );
};
